<script>
  import { onMount, onDestroy } from 'svelte';
  import { Link } from 'svelte-routing';
  import { login } from '../authService';
  import { t } from '../i18nStore'; // Import localization

  // State
  let isVisible = false;
  let currentSlide = 0;
  let interval;

  // Slide Data (Using translation keys)
  const slides = [
    {
      titleKey: "carousel.s1_title",
      descKey: "carousel.s1_desc",
      bgColor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", // Blue/Purple
      icon: "🔍"
    },
    {
      titleKey: "carousel.s2_title",
      descKey: "carousel.s2_desc",
      bgColor: "linear-gradient(135deg, #2af598 0%, #009efd 100%)", // Green/Blue
      icon: "💰"
    },
    {
      titleKey: "carousel.s3_title",
      descKey: "carousel.s3_desc",
      bgColor: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 99%, #fecfef 100%)", // Pink/Orange
      icon: "🚀"
    }
  ];

  onMount(() => {
    const dismissed = localStorage.getItem('intro_dismissed');
    if (!dismissed) {
      isVisible = true;
      startTimer();
    }
  });

  onDestroy(() => {
    stopTimer();
  });

  function startTimer() {
    stopTimer();
    interval = setInterval(() => {
      nextSlide();
    }, 5000); 
  }

  function stopTimer() {
    if (interval) clearInterval(interval);
  }

  function nextSlide() {
    currentSlide = (currentSlide + 1) % slides.length;
  }

  function setSlide(index) {
    currentSlide = index;
    startTimer(); 
  }

  function dismiss() {
    isVisible = false;
    localStorage.setItem('intro_dismissed', 'true');
  }

  function handleLogin() {
    login();
  }
</script>

{#if isVisible}
  <div class="carousel-container">
    <button class="close-btn" on:click={dismiss} aria-label="Dismiss">✕</button>
    
    {#each slides as slide, index}
      <div 
        class="slide {index === currentSlide ? 'active' : ''}" 
        style="background: {slide.bgColor};"
      >
        <div class="content">
          <div class="icon">{slide.icon}</div>
          <h2>{$t(slide.titleKey)}</h2>
          <p>{$t(slide.descKey)}</p>
          
          <div class="actions">
            {#if index === 2}
                 <!-- UPDATED: Point to /claim and use unified class -->
                 <Link to="/claim" class="btn btn-unified">{$t('carousel.btn_claim')}</Link>
            {:else}
                 <button class="btn btn-unified" on:click={handleLogin}>{$t('carousel.btn_member')}</button>
            {/if}
          </div>
        </div>
      </div>
    {/each}

    <!-- Indicators -->
    <div class="indicators">
      {#each slides as _, index}
        <button 
          class="dot {index === currentSlide ? 'active' : ''}" 
          on:click={() => setSlide(index)}
        ></button>
      {/each}
    </div>
  </div>
{/if}

<style>
  .carousel-container {
    position: relative;
    width: 100%;
    /* Increased height slightly to prevent cramping */
    height: 35vh; 
    min-height: 320px;
    overflow: hidden;
    margin-bottom: 2rem;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
    border-radius: 0 0 12px 12px;
  }

  @media (max-width: 768px) {
    .carousel-container {
      height: 50vh;
      border-radius: 0; 
    }
  }

  .slide {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity 0.8s ease-in-out;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: white;
    /* FIX OVERLAP: Add padding bottom to clear the indicators */
    padding: 2rem 2rem 4rem 2rem; 
    box-sizing: border-box;
  }

  .slide.active {
    opacity: 1;
    z-index: 1;
  }

  .content {
    max-width: 800px;
    z-index: 2;
  }

  .icon {
    font-size: 3rem;
    margin-bottom: 0.5rem;
    text-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }

  h2 {
    font-size: 2.2rem;
    margin: 0 0 1rem 0;
    text-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }

  p {
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
    line-height: 1.5;
    text-shadow: 0 1px 2px rgba(0,0,0,0.2);
  }

  .actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
  }

  .btn {
    padding: 0.8rem 2rem;
    border-radius: 30px;
    font-weight: bold;
    text-decoration: none;
    cursor: pointer;
    border: none;
    font-size: 1rem;
    transition: transform 0.2s, box-shadow 0.2s;
    box-shadow: 0 4px 6px rgba(0,0,0,0.2);
    /* Ensure Link elements behave like buttons */
    display: inline-block; 
  }

  .btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0,0,0,0.3);
  }

  /* Unified Button Style (White Background) */
  .btn-unified {
    background-color: white;
    color: #333;
  }

  .close-btn {
    position: absolute;
    top: 15px;
    right: 15px;
    background: rgba(0, 0, 0, 0.2);
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    z-index: 10;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
  }

  .close-btn:hover {
    background: rgba(0, 0, 0, 0.5);
  }

  .indicators {
    position: absolute;
    bottom: 20px;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    z-index: 10;
  }

  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.4);
    border: none;
    cursor: pointer;
    transition: background 0.3s;
  }

  .dot.active {
    background: white;
    transform: scale(1.2);
  }

  @media (max-width: 600px) {
    h2 { font-size: 1.8rem; }
    p { font-size: 0.95rem; }
    .icon { font-size: 2.5rem; }
  }
</style>